import { useEffect, useRef, useState, type RefObject } from 'react';
import { Pause, Play } from 'lucide-react';
import { clock } from './transcriptsApi';

// A RECORDING'S PLAYER: a big round play button, a seek bar, the time, the speed. The
// browser's own <audio controls> is a grey strip whose play button is easy to miss and
// whose look changes from one browser to the next; this is the same element, hidden, with
// controls that read as the one thing to press.
//
// `audioRef` is for a parent that steers it — the reader seeks to a turn's timestamp — and
// `onTime` for one that follows it (the reader lights the turn being heard).

const SPEEDS = [1, 1.25, 1.5, 2];

export function AudioPlayer({ src, audioRef, autoPlay = false, onTime }: {
  src: string;
  audioRef?: RefObject<HTMLAudioElement | null>;
  autoPlay?: boolean;
  onTime?: (seconds: number) => void;
}) {
  const own = useRef<HTMLAudioElement>(null);
  const ref = audioRef ?? own;
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);

  // A new recording starts from nothing, not from where the last one stopped.
  useEffect(() => {
    setTime(0);
    setDuration(0);
    setPlaying(false);
  }, [src]);

  useEffect(() => {
    if (ref.current) ref.current.playbackRate = speed;
  }, [speed, ref, src]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };

  const seek = (seconds: number) => {
    const el = ref.current;
    if (el) el.currentTime = seconds;
  };

  return (
    <div className="flex w-full items-center gap-3">
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        autoPlay={autoPlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => {
          setTime(e.currentTarget.currentTime);
          onTime?.(e.currentTarget.currentTime);
        }}
        className="hidden"
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-500"
      >
        {playing
          ? <Pause className="h-5 w-5" fill="currentColor" />
          : <Play className="ml-0.5 h-5 w-5" fill="currentColor" />}
      </button>
      <input
        type="range"
        min={0}
        max={duration || 0}
        step={0.1}
        value={Math.min(time, duration || 0)}
        onChange={(e) => seek(Number(e.target.value))}
        className="min-w-0 flex-1 cursor-pointer accent-blue-600"
        aria-label="Position"
      />
      <span className="shrink-0 font-mono text-xs tabular-nums text-slate-500">
        {clock(time)} / {duration ? clock(duration) : '--:--'}
      </span>
      <button
        type="button"
        onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
        title="Speed"
        className="w-12 shrink-0 rounded-md border border-slate-300 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
      >
        {speed}×
      </button>
    </div>
  );
}
